//
// DISCLAIMER
//
// Copyright 2026 ArangoDB GmbH, Cologne, Germany
//

import React, { Component } from "react";
import { Icon, Input, InputOnChangeData, Label, Loader, Menu, Message, Table } from "semantic-ui-react";
import apiClients from "../../api/apiclients";
import { CreditPlan as ApiCreditPlan, CreditPlanList as ApiCreditPlanList, ListCreditPlansRequest as ApiListCreditPlansRequest } from "../../api/lib";
import { RouteComponentProps } from "react-router-dom";
import { withRefresh, IWithRefreshProps } from "../../util/WithRefresh";
import { Loading, LoaderBoxForTable as LoaderBox, ContentSegment, SecondaryMenu, PagingButtons, ErrorMessage, ListActionView, TextLink } from "../../ui/lib";

import { DateTimePopupWithUTCAndLocalTime } from "../../util/dateAndTimeUtils/DateTime";
import { Routes } from "../../routes";

// Arguments for header view
interface IHeaderView {
  loading: boolean;
  onNameChanged: (name: string) => void;
  page: number;
  pageSize: number;
  count: number;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
}

const HeaderView = ({ ...args }: IHeaderView) => (
  <Table.Header>
    <Table.Row>
      <Table.HeaderCell>
        <Input
          label="Name"
          icon="search"
          size="mini"
          placeholder="Search ..."
          onChange={(event: React.SyntheticEvent<HTMLInputElement, Event>, data: InputOnChangeData) => {
            args.onNameChanged(data.value);
          }}
        />
      </Table.HeaderCell>
      <Table.HeaderCell>Description</Table.HeaderCell>
      <Table.HeaderCell>Lock state</Table.HeaderCell>
      <Table.HeaderCell>Starts at</Table.HeaderCell>
      <Table.HeaderCell>Ends at</Table.HeaderCell>
      <Table.HeaderCell>Created</Table.HeaderCell>
      <Table.HeaderCell>
        Actions
        <PagingButtons {...args} />
        <LoaderBox>
          <Loader size="mini" active={args.loading} inline />
        </LoaderBox>
      </Table.HeaderCell>
    </Table.Row>
  </Table.Header>
);

// Interface describing a credit plan row
interface IRowView {
  item: ApiCreditPlan;
  isActive: boolean;
  onClickView: () => void;
}

const RowView = ({ ...args }: IRowView) => (
  <Table.Row positive={args.isActive}>
    <Table.Cell>
      <TextLink label={args.item.name || args.item.id} href={Routes.dashboard_sales_creditPlan_detailsWithId(args.item.id as string)} />
      {args.isActive && (
        <Label color="green" size="tiny" horizontal>
          Active
        </Label>
      )}
    </Table.Cell>
    <Table.Cell>{args.item.description}</Table.Cell>
    <Table.Cell>
      <Icon name={args.item.locked ? "lock" : "lock open"} />
    </Table.Cell>
    <Table.Cell>{args.item.starts_at ? <DateTimePopupWithUTCAndLocalTime dateTime={args.item.starts_at} label="Starts at" /> : "-"}</Table.Cell>
    <Table.Cell>{args.item.ends_at ? <DateTimePopupWithUTCAndLocalTime dateTime={args.item.ends_at} label="Ends at" /> : "-"}</Table.Cell>
    <Table.Cell>
      <DateTimePopupWithUTCAndLocalTime dateTime={args.item.created_at} label="Created at" />
    </Table.Cell>
    <Table.Cell textAlign="right" collapsing>
      <div className="table-action-buttons">
        <ListActionView onClick={args.onClickView} />
      </div>
    </Table.Cell>
  </Table.Row>
);

// Interface describing the credit plan list
interface IListView {
  items: ApiCreditPlan[];
  activeId?: string;
  loading: boolean;
  onNameChanged: (name: string) => void;
  page: number;
  pageSize: number;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  onClickView: (planId: string) => void;
}

const ListView = ({ ...args }: IListView) => (
  <Table striped>
    <HeaderView {...args} count={args.items.length} />
    <Table.Body>
      {args.items.map((item) => (
        <RowView
          {...args}
          key={item.id}
          item={item}
          isActive={!!args.activeId && item.id === args.activeId}
          onClickView={() => args.onClickView(item.id || "")}
        />
      ))}
    </Table.Body>
  </Table>
);

// Determines which of the given plans is currently active: the plan with the
// latest start date in the past whose end date (if any) has not passed — mirrors
// pricingd's GetActiveCreditPlan. Computed over the loaded page only, so it marks
// the active plan when it is on the current view.
const activeCreditPlanId = (items: ApiCreditPlan[]): string | undefined => {
  const now = Date.now();
  let activeId: string | undefined;
  let activeStart = -Infinity;
  for (const p of items) {
    if (!p.starts_at) continue;
    const start = new Date((p.starts_at as unknown) as string).getTime();
    if (isNaN(start) || start > now) continue;
    if (p.ends_at) {
      const end = new Date((p.ends_at as unknown) as string).getTime();
      if (!isNaN(end) && end <= now) continue;
    }
    if (start > activeStart || (start === activeStart && (p.id || "") > (activeId || ""))) {
      activeId = p.id;
      activeStart = start;
    }
  }
  return activeId;
};

interface IEmptyView {
  page: number;
}

const EmptyView = ({ ...args }: IEmptyView) => <div>{args.page > 0 ? "No more credit plans" : "No credit plans selected"}</div>;

// Interface describing the credit plan list view arguments
export interface ICreditPlanListViewArgs extends RouteComponentProps {
  loading: boolean;
  plans?: ApiCreditPlanList;
  onNameChanged: (name: string) => void;
  page: number;
  pageSize: number;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  onClickView: (planId: string) => void;
}

export const CreditPlanListView = ({ ...args }: ICreditPlanListViewArgs) => {
  if (!args.plans) {
    return <Loading />;
  }
  const isEmpty = !args.plans.items || args.plans.items.length === 0;
  const plans = args.plans ? args.plans.items : undefined;
  const activeId = activeCreditPlanId(plans || []);
  return (
    <div>
      <Message info icon="info circle">
        <Message.Content>
          <Message.Header>How credit plans work</Message.Header>
          <p>
            Credit plans are versioned by effective date (<code>starts_at</code>).
          </p>
          <p>
            A plan can be edited in the Config repo (<code>controlplane/controlplane/creditplans/</code>) only until its <strong>start date</strong>. On the{" "}
            <code>starts_at</code> date the plan <strong>automatically</strong> becomes active and is <strong>locked</strong> (immutable) — this happens on its
            own, with no manual step; billing then relies on its rates.
          </p>
          <p>
            To change pricing, add a <strong>new dated CreditPlan file</strong> in the Config repo — the plan with the most recent start date that has already
            passed is the active one.
          </p>
        </Message.Content>
      </Message>
      <ListView {...args} items={plans || []} activeId={activeId} />
      {isEmpty && <EmptyView page={args.page} />}
    </div>
  );
};

// Interface decribing the properties of the credit plan list component
interface ICreditPlanListProps extends IWithRefreshProps, RouteComponentProps {
  onViewCreditPlan: (planID: string) => void;
}

// Interface decribing the state of the credit plan list component
interface ICreditPlanListState {
  plans?: ApiCreditPlanList;
  nameFilter: string;
  page: number;
  pageSize: number;
}

// The component to show credit plans as a read-only list.
class CreditPlanList extends Component<ICreditPlanListProps, ICreditPlanListState> {
  state: ICreditPlanListState = {
    plans: undefined,
    nameFilter: "",
    page: 0,
    pageSize: 10,
  };

  reloadPlans = async () => {
    var req: ApiListCreditPlansRequest = {
      options: {
        page: this.state.page,
        page_size: this.state.pageSize,
      },
    };
    const plans = await apiClients.idashboardClient.ListCreditPlans(req);
    this.setState({ plans: plans });
  };

  refreshPlans = () => {
    this.props.refreshNow && this.props.refreshNow(this.reloadPlans);
  };

  componentDidMount() {
    this.refreshPlans();
  }

  onNameFilterChanged = (name: string) => {
    this.setState(
      {
        nameFilter: name,
        page: 0,
      },
      this.refreshPlans
    );
  };

  onNextPage = () => {
    this.setState(
      (prev) => ({
        page: prev.page + 1,
      }),
      this.refreshPlans
    );
  };

  onPreviousPage = () => {
    this.setState(
      (prev) => ({
        page: prev.page - 1,
      }),
      this.refreshPlans
    );
  };

  render() {
    const plans = this.state.plans;
    const page = this.state.page;

    return (
      <ContentSegment>
        <ErrorMessage active={!!this.props.error} onDismiss={this.props.clearError} message={this.props.error} />
        <SecondaryMenu>
          <Menu.Item header>Credit plans</Menu.Item>
        </SecondaryMenu>
        <CreditPlanListView
          plans={plans}
          onNameChanged={this.onNameFilterChanged}
          onNextPage={this.onNextPage}
          onPreviousPage={page > 0 ? this.onPreviousPage : undefined}
          page={page}
          pageSize={this.state.pageSize}
          onClickView={this.props.onViewCreditPlan}
          {...this.props}
        />
      </ContentSegment>
    );
  }
}

export default withRefresh()(CreditPlanList);
